import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, Routes } from '@angular/router';
import { map } from 'rxjs';
import { AdminLayoutComponent } from './@theme/shared/components/layouts/admin-layout/admin-layout.component';
import { AuthLayoutComponent } from './@theme/shared/components/layouts/auth-layout/auth-layout.component';
import { AccessDeniedComponent } from './auth/access-denied/access-denied.component';
import { AuthCallbackComponent } from './auth/auth-callback/auth-callback.component';
import { AppLoginComponent } from './auth/login/login.component';
import { AccessRightsService } from './core/access-rights/access-right.service';
import { authGuard } from './core/auth/guard/activate-auth.guard';
import { AppNavigationService } from './core/navigation/app-navigation.service';
import { HomeComponent } from './home/home.component';
import { GeneralApplicationsDashboardComponent } from './dashboard/general-applications-dashboard/general-applications-dashboard.component';
import { ReuseComponent } from './shared/routing/routing.model';

export const routes: Routes = [
  {
    path: 'public/feedback-request',
    loadChildren: () => import('./public/feedback-request/feedback-request.routes').then(m => m.FEEDBACK_REQUEST_ROUTES),
  },
  {
    path: '',
    redirectTo: 'home',
    pathMatch: 'full'
  },
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'login',
        component: AppLoginComponent
      },
      {
        path: 'auth-callback',
        component: AuthCallbackComponent
      },
      {
        path: 'access-denied',
        component: AccessDeniedComponent
      },
      {
        path: '',
        loadChildren: () => import('./privacy/privacy.routes').then(m => m.PRIVACY_ROUTES),
      }
    ]
  },
  {
    path: '',
    component: AdminLayoutComponent,
    canActivateChild: [authGuard],
    resolve: {
      // load access-rights before loading any child route
      ['access-rights']: (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
        const accessRightsService = inject(AccessRightsService);
        const appNavigationService = inject(AppNavigationService);

        return accessRightsService.ensureHasAccessRights().pipe(
          map(r => {
            if (!r) {
              appNavigationService.goToAccessDenied();
            }
          })
        );
      }
    },
    children: [
      {
        path: 'settings',
        loadChildren: () => import('./settings/settings.routes').then(m => m.SETTINGS_ROUTES),
        data: { title: 'Settings', breadcrumb: 'Settings' }
      },
      {
        path: 'job-requisitions',
        loadChildren: () => import('./job-requisitions/job-requisitions.routes').then(m => m.JOB_REQUISITIONS_ROUTES),
        data: { title: 'Job Requisitions', breadcrumb: 'Job Requisitions' }
      },
      {
        path: 'hiring-requests',
        loadChildren: () => import('./hiring-requests/hiring-requests.routes').then(m => m.HIRING_REQUESTS_ROUTES),
        data: { title: 'Hiring Requests', breadcrumb: 'Hiring Requests' }
      },
      {
        path: 'announcements',
        loadChildren: () => import('./announcements/announcements.routes').then(m => m.ANNOUNCEMENTS_ROUTES),
        data: { title: 'Announcements', breadcrumb: 'Announcements' }
      },
      {
        path: 'recruitment-events',
        loadChildren: () => import('./recruitment-events/recruitment-events.routes').then(m => m.RECRUITMENT_EVENTS_ROUTES),
        data: { title: 'Recruitment Events', breadcrumb: 'Recruitment Events' }
      },
      {
        path: 'candidates',
        loadChildren: () => import('./candidates/candidates.routes').then(m => m.CANDIDATES_ROUTES),
        data: { title: 'Candidates' }
      },
      {
        path: 'emails',
        loadChildren: () => import('./emails/emails.routes').then(m => m.EMAILS_ROUTES),
        data: { title: 'Email Logs' }
      },
      {
        path: 'reports',
        loadChildren: () => import('./reports/reports.routes').then(m => m.REPORTS_ROUTES),
        data: { title: 'Reports' }
      },
      {
        path: 'dashboard/general-applications', component: GeneralApplicationsDashboardComponent,
        data: {
          title: 'General Applications',
          reuse: true,
          reuseComponentId: ReuseComponent.GeneralApplicationsDashboardComponent
        }
      },
      { path: 'home', component: HomeComponent },
      {
        path: 'website-popup',
        loadChildren: () => import('./website-popup/website-popup.routes').then(m => m.WEBSITE_POPUP_ROUTES),
        data: { title: 'Website Popups', breadcrumb: 'Website Popups' }
      },
    ]
  },
  { path: '**', redirectTo: '/home' }, // Default route
];
